import { useState, useEffect } from "react";
import { Settings } from "lucide-react";
import axiosInstance, { getUserRole } from "../../API/interceptor";
import { usePermission } from "../../permission/PermissionContext";
import SetPriorityTab         from "./tabs/SetPriorityTab";
import TaskStatusTab          from "./tabs/TaskStatusTab";
import WorkspaceManagementTab from "./tabs/WorkspaceManagementTab";
import WorkspaceTemplatesTab  from "./tabs/WorkspaceTemplatesTab";

const SKELETON_WIDTHS = ["w-[160px]", "w-[120px]", "w-[180px]", "w-[170px]"];

export default function SettingsPage() {
  const [role,      setRole]      = useState(null);
  const [activeTab, setActiveTab] = useState(null);

  const setPriorityPerm         = usePermission("set_priority_listing");
  const workspaceManagementPerm = usePermission("work_space_management");
  const permsLoading = setPriorityPerm === "loading" || workspaceManagementPerm === "loading";

  useEffect(() => {
    getUserRole()
      .then(res => {
        const { is_admin, is_product_owner, can_view_task_status } = res.data;
        setRole({ is_admin, is_product_owner, can_view_task_status });
      })
      .catch(() => {
        setRole({ is_admin: false, is_product_owner: false, can_view_task_status: false });
      });
  }, []);

  const hasTaskStatus            = role?.can_view_task_status || false;
  const hasSetPriority           = setPriorityPerm === true;
  const hasWorkspaceManagement   = workspaceManagementPerm === true;

  // Tab order: Set Priority List → Task Status (admin only) → Workspace Management → Workspace Templates
  const TABS = [
    ...(hasSetPriority ? ["Set Priority List"] : []),
    ...(hasTaskStatus ? ["Task Status"] : []),
    ...(hasWorkspaceManagement ? ["Workspace Management"] : []),
    "Workspace Templates",
  ];

  // Default to the first visible tab once role + permissions have resolved
  useEffect(() => {
    if (!role || permsLoading) return;
    if (!activeTab || !TABS.includes(activeTab)) {
      setActiveTab(TABS[0] || null);
    }
  }, [role, permsLoading, hasSetPriority, hasTaskStatus, hasWorkspaceManagement]);

  // ── Loading skeleton ─────────────────────────────────────────
  if (!role || permsLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
          {SKELETON_WIDTHS.map((w, i) => (
            <div key={i} className={`h-8 bg-gray-100 rounded-lg animate-pulse ${w}`} />
          ))}
        </div>
        <div className="p-6 space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-10 bg-gray-50 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">

      {/* ── Tab header ── */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-white">
        <div className="flex overflow-x-auto min-w-0 flex-shrink scrollbar-hide">
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3 md:px-4 py-3.5 text-xs md:text-sm font-semibold whitespace-nowrap border-none bg-transparent cursor-pointer transition-all -mb-px border-b-2 ${
                activeTab === tab ? "text-violet-600 font-bold border-[#7C3AED]" : "text-slate-500 hover:text-slate-700 border-transparent"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
        <div className="hidden sm:flex items-center gap-2 text-xs text-slate-400">
          <Settings size={13} />
          <span>Task Flow Configuration</span>
        </div>
      </div>

      {/* ── Tab content ── */}
      <div className="p-5">
        {activeTab === "Set Priority List"      && hasSetPriority && <SetPriorityTab />}
        {activeTab === "Task Status"            && hasTaskStatus && <TaskStatusTab />}
        {activeTab === "Workspace Management"   && hasWorkspaceManagement && <WorkspaceManagementTab />}
        {activeTab === "Workspace Templates"    && <WorkspaceTemplatesTab />}
      </div>

    </div>
  );
}